/**
 * One task as a row: tick, title, where it belongs (source, goal, person,
 * affiliate), when it's due, who owns it and whether an AI employee does it.
 * Click anywhere on it to open the task.
 */
import { Link, useLocation } from "react-router-dom";
import { BookOpen, Bot, Flag, Handshake, ListChecks, Repeat, UserRound } from "lucide-react";
import { Avatar, Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import { TickButton, dotClass, dueLabel, firstName } from "@/features/command/ui";
import { agentName, dueTime, repeatLabel } from "./lib";
import type { Person, TeamData, WorkTask } from "./types";

export function TaskRow({
  t, onToggle, onOpen, team, people, wsTz, showOwner = true, showDue = true, dueText, nudgeHowTo, hideGoal, className,
}: {
  t: WorkTask;
  onToggle: () => void;
  onOpen: () => void;
  team: TeamData | null;
  people: Person[];
  wsTz: string;
  showOwner?: boolean;
  showDue?: boolean;
  /** Replace the due label (Routines shows the rhythm instead). */
  dueText?: { text: string; tone: string; sub?: string } | null;
  /** Flag a missing how-to (routines should be hand-off-able). */
  nudgeHowTo?: boolean;
  /** Inside a goal, its own name is noise. */
  hideGoal?: boolean;
  className?: string;
}) {
  const { pathname } = useLocation();
  const done = t.status === "completed";
  const due = dueText ?? dueLabel(t.due_at);
  const time = dueTime(t, wsTz);
  const owner = people.find((p) => p.id === t.assignee_id);
  const steps = t.checklist ?? [];
  const stepsDone = steps.filter((s) => s.done).length;
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}
      className={cn("group flex cursor-pointer items-start gap-3 px-4 py-2.5 transition-colors hover:bg-zinc-800/25 focus:bg-zinc-800/25 focus:outline-none", className)}
    >
      <div className="pt-0.5"><TickButton done={done} onToggle={onToggle} /></div>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          {!done && (t.priority === "urgent" || t.priority === "high") && (
            <span className={cn(
              "shrink-0 rounded-full px-1.5 py-px text-[10px] font-semibold",
              t.priority === "urgent" ? "bg-rose-500/15 text-rose-300" : "bg-amber-500/15 text-amber-300",
            )}>
              {t.priority === "urgent" ? "Urgent" : "High"}
            </span>
          )}
          <span className={cn("truncate text-sm", done ? "text-zinc-500 line-through" : "text-zinc-100")}>{t.title}</span>
          {t.recurrence && (
            <Tooltip content={`Repeats ${repeatLabel(t.recurrence)}`}>
              <Repeat className="h-3 w-3 shrink-0 text-zinc-500" aria-label="Repeats" />
            </Tooltip>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-zinc-500">
          {t.agent_slug && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/12 px-1.5 py-px font-medium text-indigo-200 ring-1 ring-inset ring-indigo-400/25">
              <Bot className="h-3 w-3" />{agentName(t.agent_slug, team)}
            </span>
          )}
          {t.source_id && t.source_name && (
            <Link to={`/traffic/${t.source_id}`} state={{ from: pathname }} onClick={stop} className="inline-flex items-center gap-1 hover:text-zinc-200">
              <span className={cn("h-1.5 w-1.5 rounded-full", dotClass(t.source_color))} />{t.source_name}
            </Link>
          )}
          {t.goal_title && !hideGoal && <span className="inline-flex items-center gap-1"><Flag className="h-3 w-3" />{t.goal_title}</span>}
          {t.contact_id && t.contact_name && (
            <Link to={`/contacts/${t.contact_id}`} state={{ from: pathname }} onClick={stop} className="inline-flex items-center gap-1 hover:text-zinc-200">
              <UserRound className="h-3 w-3" />{t.contact_name}
            </Link>
          )}
          {t.affiliate_id && t.affiliate_name && (
            <Link to={`/affiliates/${t.affiliate_id}`} state={{ from: pathname }} onClick={stop} className="inline-flex items-center gap-1 hover:text-zinc-200">
              <Handshake className="h-3 w-3" />{t.affiliate_name}
            </Link>
          )}
          {steps.length > 0 && <span className="inline-flex items-center gap-1 tabular-nums"><ListChecks className="h-3 w-3" />{stepsDone}/{steps.length}</span>}
          {t.handoff_url && <span className="inline-flex items-center gap-1" title="Has a how-to"><BookOpen className="h-3 w-3" />how-to</span>}
          {nudgeHowTo && !t.handoff_url && <span className="inline-flex items-center gap-1 text-amber-300/70" title="Add the doc or video that lets anyone do this"><BookOpen className="h-3 w-3" />no how-to yet</span>}
        </div>
      </div>
      {showDue && (
        <div className="shrink-0 pt-0.5 text-right text-xs tabular-nums">
          <div className={cn(
            due.tone === "late" && !done ? "text-rose-300" : due.tone === "today" ? "text-indigo-200" : due.tone === "rhythm" ? "text-indigo-300" : "text-zinc-500",
          )}>
            {due.text}
          </div>
          {dueText?.sub ? <div className="text-[10px] text-zinc-500">{dueText.sub}</div> : time && !dueText ? <div className="text-[10px] text-zinc-500">{time}</div> : null}
        </div>
      )}
      {showOwner && (
        <Tooltip content={owner ? `${owner.name} owns this` : "Nobody owns this yet"}>
          <div className="pt-px">
            {owner ? <Avatar name={owner.name} email={owner.email} src={owner.image} size="xs" /> : (
              <div className="flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-zinc-700 text-[9px] text-zinc-500">?</div>
            )}
          </div>
        </Tooltip>
      )}
    </div>
  );
}

/** A small uppercase label between groups of rows. */
export function GroupLabel({ label, tone = "muted", count, action }: { label: string; tone?: "late" | "today" | "muted" | "brand"; count?: number; action?: React.ReactNode }) {
  return (
    <div className={cn(
      "flex items-center gap-2 px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider",
      tone === "late" ? "text-rose-300" : tone === "today" ? "text-indigo-300" : tone === "brand" ? "text-indigo-200" : "text-zinc-500",
    )}>
      {label}
      {count != null && count > 0 && <span className="rounded-full bg-zinc-800 px-1.5 py-px text-[10px] tabular-nums text-zinc-400">{count}</span>}
      {action && <span className="ml-auto normal-case tracking-normal">{action}</span>}
    </div>
  );
}

export const ownerFirst = (people: Person[], id: string | null) => firstName(people.find((p) => p.id === id)?.name);

/** A future repeat of a routine: faint, not tickable yet, opens the routine. */
export function GhostRow({ t, onOpen, people }: { t: WorkTask; onOpen: () => void; people: Person[] }) {
  const owner = people.find((p) => p.id === t.assignee_id);
  return (
    <button type="button" onClick={onOpen} className="flex w-full items-center gap-3 px-4 py-2 text-left opacity-60 transition-opacity hover:opacity-100">
      <div className="h-5 w-5 shrink-0 rounded-full border border-dashed border-zinc-700" />
      <span className="min-w-0 flex-1 truncate text-sm text-zinc-300">{t.title}</span>
      <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-zinc-500"><Repeat className="h-3 w-3" />{repeatLabel(t.recurrence)}</span>
      {owner && <Avatar name={owner.name} email={owner.email} src={owner.image} size="xs" />}
    </button>
  );
}
